import { FormWizardTemplate, FormWizardTitle, SwitchWithLabel } from "@iventis/components";
import { createInitialState, findNode, TreeBrowserDomainNodeContext, UnionOfNodes, useTreeBrowserInitialise } from "@iventis/tree-browser";
import React, { FunctionComponent, useContext, useMemo, useState } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { Body1, Body2, inlineTextIconMargin, styled } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { canEdit } from "@iventis/utilities";
import { useProjectLimiter } from "@iventis/permissions/src/project-limiter-hook";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { Theme } from "@emotion/react";
import { useTheme } from "@mui/material";
import { v4 as uuid } from "uuid";
import { createMapsCopyNotification, NotificationContext } from "@iventis/notifications";
import { MapsCopiedStatus } from "@iventis/domain-model/model/mapsCopiedStatus";
import { getSpatialLibraries } from "../services/libraries";
import { RootState } from "../state/root.store";
import { libraryNodeServices } from "../services/library-api-functions";
import { libraryWebSockets } from "./library.machine.web-sockets";
import { getSort } from "./library-get-initial-values";
import { EmbeddedLibraryComponent, EmbeddedLibraryModal, StyledLibraryContainer } from "./embedded-library";
import { libraryNodeTypeFontawesome } from "./library-nodes";

// Define the enum
const PageAction = Object.freeze({
    NEXT: "NEXT",
    BACK: "BACK",
});

/**
 * Returns a new location through onConfirm, which returns the nodesToCopy if provided and a new parentId
 */
export const LibraryCopyDialog: FunctionComponent<{
    closeModal: () => void;
    isOpen: boolean;
    treeId: string;
    onConfirm: (nodes: UnionOfNodes<Node>[], copyId: string, newParentId: string, includeMapObjects: boolean, keepPermissions: boolean) => void;
    nodesToCopy?: Node[];
    canSwitchLibrary: boolean;
}> = ({ closeModal, isOpen, treeId, onConfirm, nodesToCopy = [], canSwitchLibrary }) => {
    const translate = useIventisTranslate();
    const auth = useSelector((s: RootState) => s.auth);

    const { data: libraryNodes } = useQuery(["libraryNodes"], async () => getSpatialLibraries(), {
        initialData: [],
    });
    const { createNotification } = useContext(NotificationContext);
    const [treeBrowserState, treeBrowserContext] = useTreeBrowserInitialise(
        createInitialState<Node>({ sort: getSort(auth.user.userId), treeId, singleSelectionOnly: true, selectedNodeIds: [treeId] }),
        libraryNodeServices,
        libraryWebSockets
    );

    const canSelectNode = (node: Node) => {
        if (node.type !== NodeType.Folder) {
            return { disabled: true, tooltip: translate(Content.map10.copy.tooltip[node.type]) };
        }
        if (!canEdit(node.authorisation)) {
            return { disabled: true };
        }
        return { disabled: false, tooltip: undefined };
    };

    const header = () => {
        if (nodesToCopy?.length && nodesToCopy?.length >= 1) {
            return (
                <TextIconContainer>
                    {nodesToCopy?.length === 1 && <FontAwesomeIcon icon={libraryNodeTypeFontawesome[nodesToCopy[0].type]} />}
                    {nodesToCopy?.length > 1 ? <span>{translate(Content.library2.copy_node.multiple_items)}</span> : <Body2>{nodesToCopy[0]?.name}</Body2>}
                </TextIconContainer>
            );
        }
        return undefined;
    };

    const title = {
        0:
            nodesToCopy?.length && nodesToCopy?.length >= 1
                ? nodesToCopy?.length === 1
                    ? translate(Content.library2.copy_node.title, { nodeType: translate(Content.library.node_names[nodesToCopy[0].type])?.toLocaleLowerCase?.() })
                    : translate(Content.library2.copy_node.multi_title)
                : translate(Content.library2.copy_node.empty_title),
        1: translate(Content.library2.copy_node.options_title),
    };

    const isSharedLibrary = treeBrowserState?.tree?.type === NodeType.MappingLibrary;

    const [canUse] = useProjectLimiter();

    const reachedTeamMapLimit = isSharedLibrary && !canUse("teamMaps");

    const [page, setPage] = useState(0);
    const updatePage = (action: typeof PageAction[keyof typeof PageAction]) => {
        setPage((page) => {
            switch (action) {
                case PageAction.NEXT:
                    return page + 1;
                case PageAction.BACK:
                    return page - 1;
                default:
                    return page;
            }
        });
    };

    const [includeMapObjects, setIncludeMapObjects] = useState(true);
    const [keepPermissions, setKeepPermissions] = useState(false);
    const theme = useTheme<Theme>();

    const dialogStyle = {
        0: { height: "80vh", minWidth: undefined },
        1: { height: "400px", minWidth: "450px" },
    };

    const copyToNode = useMemo(
        () => (treeBrowserState != null ? findNode([treeBrowserState.tree], treeBrowserState.selectedNodeIds?.[0] ?? treeBrowserState.mainNodeId) : undefined),
        [treeBrowserState.selectedNodeIds, treeBrowserState.mainNodeId]
    );

    return (
        <EmbeddedLibraryModal open={isOpen} close={closeModal} style={dialogStyle[page]} testid="library-copy-modal">
            <FormWizardTemplate
                title={<FormWizardTitle header={header()} title={title[page] ?? ""} />}
                currentStage={page}
                stages={[
                    {
                        isValid: !reachedTeamMapLimit && !treeBrowserState.isLoadingTree && canEdit(copyToNode?.authorisation),
                        primaryButtonText: nodesToCopy?.length >= 1 ? translate(Content.map10.copy.copyTo) : translate(Content.map10.copy.setTo),
                        disabledSubmitButtonText: translate(Content.library2.copy_node.no_permission_tooltip),
                        primaryButtonCallback: () => {
                            updatePage(PageAction.NEXT);
                        },
                        submitButtonDataCy: "copy-to-this-location",
                        secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: closeModal }],
                    },
                    {
                        isValid: !reachedTeamMapLimit && !treeBrowserState.isLoadingTree && canEdit(copyToNode?.authorisation),
                        primaryButtonText: translate(Content.library2.copy_node.options_submit_button),
                        disabledSubmitButtonText: translate(Content.library2.copy_node.no_permission_tooltip),
                        primaryButtonCallback: () => {
                            const copyId = uuid();
                            onConfirm(nodesToCopy, copyId, copyToNode?.id, includeMapObjects, keepPermissions);
                            incrementMonitoringCount({ name: MetricName.PLANS_COPIED });
                            createNotification(
                                createMapsCopyNotification(auth.user.userId, auth.user.projectId, copyId, {
                                    status: MapsCopiedStatus.Processing,
                                    destinationId: copyToNode.id,
                                    destinationName: copyToNode.name,
                                    teamMapLimitReached: false,
                                    partialFailure: false,
                                })
                            );
                            closeModal();
                        },
                        submitButtonDataCy: "submit-copy-plans",
                        secondaryButtons: [{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => updatePage(PageAction.BACK) }],
                    },
                ]}
            >
                <TreeBrowserDomainNodeContext.Provider value={treeBrowserContext}>
                    <StyledLibraryContainer>
                        {treeBrowserContext != null && page === 0 && (
                            <EmbeddedLibraryComponent
                                canSwitchLibrary={canSwitchLibrary}
                                onLibrarySelectionChange={(node) => {
                                    treeBrowserContext.setTree(node);
                                }}
                                libraries={libraryNodes}
                                userId={auth.user.userId}
                                canSelectNode={canSelectNode}
                                nodesBeingEdited={nodesToCopy.map((n) => n.id)}
                            />
                        )}
                        {page === 1 && (
                            <StyledContainer>
                                <Body1>
                                    {translate(
                                        Content.library2.copy_node.options_description[isSharedLibrary ? ("map_objects_and_permissions" as const) : ("map_objects_only" as const)]
                                    )}
                                </Body1>
                                <StyledSwitchWithLabel
                                    checked={includeMapObjects}
                                    onChange={() => setIncludeMapObjects(!includeMapObjects)}
                                    label={translate(Content.library2.copy_node.copy_with_map_objects)}
                                    theme={theme}
                                    dataCy="include-map-objects-in-copy"
                                />
                                {isSharedLibrary && (
                                    <StyledSwitchWithLabel
                                        checked={keepPermissions}
                                        onChange={() => setKeepPermissions(!keepPermissions)}
                                        label={translate(Content.permissions.copyPermissions)}
                                        theme={theme}
                                        dataCy="include-permissions-in-copy"
                                    />
                                )}
                            </StyledContainer>
                        )}
                    </StyledLibraryContainer>
                </TreeBrowserDomainNodeContext.Provider>
            </FormWizardTemplate>
        </EmbeddedLibraryModal>
    );
};

const TextIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${inlineTextIconMargin};
    color: ${(props) => props.theme.shades.two};
`;

const StyledSwitchWithLabel = styled(SwitchWithLabel)`
    display: flex;
    justify-content: space-between;
    margin-left: 0;
    width: calc(100% - 8px);

    .MuiFormControlLabel-label {
        font-weight: bold;
    }
`;

const StyledContainer = styled.div`
    max-width: 500px;
    box-sizing: border-box;
    margin: 22px 0;
    padding: 0 22px;
`;
